/**
 * Industries. One entry per page under /industries/*.
 *
 * ─── FOUR, NOT EIGHT ───────────────────────────────────────────────────────────
 * `shipped: false` verticals stay in this file but do not get a page or a nav slot.
 * They are switched back on when the capability behind them is real — the same
 * mechanism as the claims gate in site.ts and the service tiers in services.ts.
 *
 * Cut, and why:
 *   gyms-and-studios          24-hour access complexity and real security exposure
 *   warehouses-and-industrial needs ride-on floor equipment we do not own
 *   schools-and-childcare     highest safeguarding bar of any vertical; the
 *                             background-check process does not exist yet
 *   government-institutional  needs a capability statement, stated insurance limits
 *                             and registrations. Revisit when the paperwork is real
 *
 * Claiming a vertical we cannot staff is the same mistake as claiming years we have
 * not been trading — it just takes one specific question to expose.
 * ───────────────────────────────────────────────────────────────────────────────
 *
 * These are fear-specific pages, not service pages. A property manager and a dental
 * practice both buy "nightly janitorial" — but they are afraid of completely different
 * things, and the page has to answer the fear they actually have.
 *
 * The `fear` line on each entry is drawn from the verbatim buyer research
 * (second-shift-cleaning/research/03-buyer-voice-reviews.md), not invented.
 */

/**
 * A claim that is only rendered once the underlying fact is true.
 *
 * `gate` names a key in SITE.claims. This exists because the FAQ answers were the last
 * place overclaiming was hiding: the medical page asserted OSHA bloodborne-pathogen
 * training records, and the property-manager page promised a COI in 24 hours. Neither
 * is true today. A buyer who asks for the training record and finds there isn't one has
 * learned everything they need to know about us.
 *
 * Where the honest version is a POLICY rather than a credential, it is written as a
 * policy and left ungated — "we don't use subcontractors" is a rule about how we hire,
 * true from the first hire onward. "Here are our training records" is a credential, and
 * credentials get gates.
 */
export interface GatedAnswer {
  q: string;
  a: string;
  gate?: ClaimGate;
}

export type ClaimGate =
  | 'insuredToCommercialLimits'
  | 'workersComp'
  | 'coiWithin24h'
  | 'backgroundChecked'
  | 'callsAnsweredLive'
  | 'salesTaxRegistered'
  | 'guaranteeCommitted'
  | 'pastClientNamingPermission';

export interface GatedDoc {
  text: string;
  gate?: ClaimGate;
}

export interface Industry {
  slug: string;
  /** False = kept for later, not published. See the note above. */
  shipped: boolean;
  name: string;
  blurb: string;
  headline: string;
  /** What this buyer is actually afraid of, in their register. */
  fear: string;
  intro: string;
  /** The specific things this buyer needs answered before they will sign. */
  answers: GatedAnswer[];
  /** Compliance or documentation this vertical expects. */
  documentation?: GatedDoc[];
}

export const INDUSTRIES: Industry[] = [
  {
    slug: 'offices',
    shipped: true,
    name: 'Offices',
    blurb: 'Professional offices, suites, and coworking floors.',
    headline: 'An office that looks the same on Friday as it did on Monday',
    fear: 'The first month is great, then it quietly slides and I am the one who has to complain.',
    intro:
      'Office cleaning fails slowly. The first few weeks are fine, then the crew changes, corners get cut, and one day the restroom is out of paper during a client visit. We build against that specific pattern.',
    answers: [
      {
        q: 'Will it be the same people every night?',
        a: 'Yes. One crew is assigned to your building and you will know their names. Cover is arranged before it is needed, so a call-out does not become a skipped visit.',
      },
      {
        q: 'How do I know they actually came?',
        a: 'Every visit is clocked in on site and closed out against your checklist. You get the record without asking for it.',
      },
      {
        q: 'What if something gets missed?',
        a: 'Tell us by noon the next business day. We fix it within 24 hours.',
      },
      {
        q: 'Am I locked into a year?',
        a: 'No. Month to month, 30 days notice either way, no early-termination fee.',
      },
    ],
  },
  {
    slug: 'medical-and-dental',
    shipped: true,
    name: 'Medical & dental',
    blurb: 'Practices, clinics, urgent care, and specialty offices.',
    headline: 'Clinical cleaning, documented the way an inspector expects',
    fear: 'A cleaner who does not understand bloodborne pathogens is a liability, not a saving.',
    intro:
      'A medical space has rules an office does not: dwell times, product registration, waste separation, and a confidentiality expectation that covers everything the crew can see. Those are the things we document.',
    answers: [
      {
        q: 'Are your crews trained on bloodborne pathogens?',
        a: 'Anyone we assign to a clinical space completes OSHA bloodborne pathogen training before their first shift, and it is refreshed annually. Ask us for the records and we will send them.',
        gate: 'backgroundChecked',
      },
      {
        q: 'What disinfectants do you use?',
        a: 'EPA-registered products, used at the dwell time on the label — which is the step most people skip and the one that decides whether it worked. We will tell you exactly what we are using in your space.',
      },
      {
        q: 'How do you handle patient confidentiality?',
        a: 'Everyone signs a confidentiality agreement before they set foot in your practice. Crews do not handle charts, do not touch workstations, and do not photograph anything in a treatment area.',
      },
      {
        q: 'Can you cover multiple locations consistently?',
        a: 'Yes — the same checklist and the same standard at each site, with one person you call about all of them.',
      },
    ],
    documentation: [
      { text: 'OSHA bloodborne pathogen training records', gate: 'backgroundChecked' },
      { text: 'EPA-registered disinfectant list with dwell times' },
      { text: 'Safety data sheets kept on site' },
      { text: 'Signed confidentiality agreements for everyone assigned' },
      { text: 'Certificate of insurance naming your practice', gate: 'insuredToCommercialLimits' },
    ],
  },
  {
    slug: 'property-managers',
    shipped: true,
    name: 'Property managers',
    blurb: 'Multi-tenant buildings, common areas, and turnover cleans.',
    headline: 'One vendor, every building, and paperwork that arrives on time',
    fear: 'Photo verification, or hard pass. I am not chasing a ghost cleaner across four properties.',
    intro:
      'Managing multiple buildings means the cleaning vendor is either an asset or a second job. What makes the difference is documentation: a certificate that arrives when you ask, a crew you can identify, and a record of every visit.',
    answers: [
      {
        q: 'How fast can I get a COI with us named as additional insured?',
        a: 'Within 24 hours of asking, for any building on the contract.',
        gate: 'coiWithin24h',
      },
      {
        q: 'Can you cover several properties on one agreement?',
        a: 'Yes. One contract, per-site scopes and schedules, one invoice, one contact.',
      },
      {
        q: 'How do you handle keys, fobs, and alarm codes?',
        a: 'Issued to named individuals only, logged, and returned on assignment change. Lock-up and alarm-set are signed off every visit.',
      },
      {
        q: 'What about a same-week turnover clean?',
        a: 'Turnovers and make-readies are quoted as project work and scheduled around your leasing calendar.',
      },
    ],
    documentation: [
      { text: 'Certificate of insurance with additional-insured endorsement', gate: 'insuredToCommercialLimits' },
      { text: 'W-9 and vendor onboarding packet' },
      { text: 'Named key and access holders, logged' },
      { text: 'Per-site scope of work with a signed checklist' },
    ],
  },
  {
    slug: 'churches',
    shipped: true,
    name: 'Churches',
    blurb: 'Sanctuaries, fellowship halls, classrooms, and nurseries.',
    headline: 'Ready by Sunday, without a volunteer staying late',
    fear: 'We cannot sign a long contract and we cannot have strangers unsupervised around the children.',
    intro:
      'Church buildings run on an unusual rhythm — heavy use in bursts, then quiet — and the people cleaning them are around classrooms and nurseries. Both facts shape how we staff it.',
    answers: [
      {
        q: 'Do we have to sign a long contract?',
        a: 'No. Month to month, 30 days notice. Plenty of congregations run us weekly and add hours around holidays.',
      },
      {
        q: 'Who is actually in our building?',
        a: 'People on our payroll — W-2 employees, background-checked before they start. We do not use subcontractors, so there is never someone in your building we have not screened ourselves.',
      },
      {
        q: 'Can you work around our schedule?',
        a: 'Yes. Most churches want us between the last midweek activity and Sunday morning. We fit that window.',
      },
      {
        q: 'What about the nursery and children’s rooms?',
        a: 'Their own scope on the checklist, with toys and soft surfaces treated separately from general areas.',
      },
    ],
  },
  {
    slug: 'gyms-and-studios',
    shipped: false,
    name: 'Gyms & studios',
    blurb: 'Fitness floors, studios, locker rooms, and 24-hour access facilities.',
    headline: 'Locker rooms that do not smell, and a door that gets locked',
    fear: 'The front door was left unlocked overnight. That is the whole risk in one sentence.',
    intro:
      'A gym is a security problem and a moisture problem at the same time. The locker rooms need real attention and the building needs closing out properly — often in a facility that never fully shuts.',
    answers: [
      {
        q: 'Can you clean around 24-hour access?',
        a: 'Yes. We schedule into your lowest-traffic window and work in sections so the floor stays usable.',
      },
      {
        q: 'How do you handle locker rooms and showers?',
        a: 'Daily disinfection of showers, benches, and lockers, with a deeper descaling and grout treatment on a set cycle.',
      },
      {
        q: 'What about the doors and alarm?',
        a: 'Lock-up and alarm-set are a signed line on the checklist, every visit, by name.',
      },
      {
        q: 'Equipment and mats?',
        a: 'Wiped and disinfected nightly, with a deeper treatment scheduled so the rubber and vinyl are not damaged.',
      },
    ],
  },
  {
    slug: 'warehouses-and-industrial',
    shipped: false,
    name: 'Warehouses & industrial',
    blurb: 'Distribution floors, production areas, and attached offices.',
    headline: 'The office side and the floor side, cleaned to different standards',
    fear: 'Most cleaners quote the square footage and then discover what a warehouse floor actually is.',
    intro:
      'A warehouse is two buildings on one slab: an office that needs an office clean, and a floor that needs equipment. Quoting it as one number is how vendors end up underbidding and cutting the crew.',
    answers: [
      {
        q: 'Do you have the right equipment for the floor?',
        a: 'Yes — ride-on and walk-behind scrubbers sized to the space, not shop brooms.',
      },
      {
        q: 'How do you price a mixed facility?',
        a: 'The office area and the production or storage area are scoped and priced separately, so neither subsidises the other.',
      },
      {
        q: 'Can you work around our shifts?',
        a: 'Yes. Most industrial clients put us between shifts or on the weekend.',
      },
      {
        q: 'What about safety requirements on our floor?',
        a: 'Crews follow your site rules — PPE, lockout zones, and traffic paths — and we will walk them before the first shift.',
      },
    ],
  },
  {
    slug: 'schools-and-childcare',
    shipped: false,
    name: 'Schools & childcare',
    blurb: 'Classrooms, daycares, cafeterias, and after-school programmes.',
    headline: 'Vetted adults, cleaned classrooms, done before drop-off',
    fear: 'Who exactly is in the building at night, and what were they checked for?',
    intro:
      'For a school or a daycare, the cleaning standard matters and the staffing standard matters more. Every person we send is an employee we screened, not a contractor we found.',
    answers: [
      {
        q: 'Who is actually in our building?',
        a: 'Background-checked W-2 employees, assigned by name, re-screened annually. Never a subcontractor.',
      },
      {
        q: 'How do you handle classroom surfaces and toys?',
        a: 'Disinfected nightly with EPA-registered products safe for the age group, with toys and soft surfaces on their own schedule.',
      },
      {
        q: 'Can you be finished before staff arrive?',
        a: 'Yes. Evening and overnight windows are the default.',
      },
      {
        q: 'What about cafeterias and restrooms?',
        a: 'Both are scoped separately with their own checklists — they are the two areas parents notice first.',
      },
    ],
    documentation: [
      { text: 'Background check on every assigned technician' },
      { text: 'EPA-registered products appropriate to the age group' },
      { text: 'Signed confidentiality and conduct agreement' },
      { text: 'Certificate of insurance naming the school' },
    ],
  },
  {
    slug: 'government-and-institutional',
    shipped: false,
    name: 'Government & institutional',
    blurb: 'Municipal buildings, agencies, and institutional facilities.',
    headline: 'Built to survive a procurement process',
    fear: 'Half the vendors who bid cannot produce the paperwork when it is time to award.',
    intro:
      'Institutional buying is a documentation exercise before it is a cleaning decision. We keep the packet current so a solicitation does not become a scramble.',
    answers: [
      {
        q: 'What is your NAICS code?',
        a: '561720 — Janitorial Services.',
      },
      {
        q: 'Can you provide a capability statement?',
        a: 'Yes, on request, with past performance, service lines, and the insurance schedule.',
      },
      {
        q: 'Do you carry the insurance limits we require?',
        a: 'Tell us the limits in the solicitation and we will confirm in writing before we bid.',
      },
      {
        q: 'How do you handle prevailing wage or reporting requirements?',
        a: 'Our technicians are W-2 employees, so wage reporting and certified payroll are straightforward rather than a workaround.',
      },
    ],
    documentation: [
      { text: 'Capability statement, one page' },
      { text: 'NAICS 561720' },
      { text: 'W-9 and insurance schedule with limits' },
      { text: 'Past performance summary' },
    ],
  },
];

/** The four we publish. */
export function shippedIndustries(): Industry[] {
  return INDUSTRIES.filter((i) => i.shipped);
}

export function industryBySlug(slug: string): Industry | undefined {
  const found = INDUSTRIES.find((i) => i.slug === slug);
  // An unshipped vertical has no page. 404, not 301 — it was never published.
  return found && found.shipped ? found : undefined;
}
